import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BOOKING_PAGE_VIEWS, GTM_EVENTS } from '@constants/app.constants';
import { CustomFieldModel } from '@models/custom-field.model';
import { PartnerDbModel } from '@models/partner-db.model';
import { UserInfo } from '@models/user.model';
import { CurrentViewData, WidgetConfModel } from '@models/widget-conf.model';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { LoggerService } from '@services/utils/logger.service';
import { normalizePostcode, resolvePersonalFormZip } from '@services/utils/store-postcode.util';
import { PersonalFormComponent } from '@ui-lib/booking-questions/personal-form/personal-form.component';

@Component({
  selector: 'app-personal-form-view',
  templateUrl: './personal-form-view.component.html',
  styleUrls: ['./personal-form-view.component.scss'],
  imports: [PersonalFormComponent]
})
export class PersonalFormViewComponent implements OnInit {

  @Input() partnerData: PartnerDbModel;
  @Output() nextPageEvent = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  // Runtime values are a mix of standard field-name strings (e.g. 'email') and numeric custom-field
  // ids (existing context.customer convention — see PersonalFormComponent.ngOnInit()); widened here
  // because inline customer-form questions and auto_load_booking_questions_placement=personal_form
  // append numeric ids at runtime.
  customerFormFields: (string | number)[] = [];
  customerRequiredFields: string[] = [];
  // Per-widget required question ids (context.required_booking_questions) → passed to
  // <app-personal-form> so a question the partner marked required in the customer step is enforced.
  requiredQuestionIds: number[] = [];
  customerReadonlyFields: string[] = [];
  customerPrefillValues: { [fieldName: string]: string } = {};
  meetingTypes: number[] = [];
  userInfo: UserInfo = new UserInfo();
  showMeetingTypes: boolean = Boolean(true);
  isGTMEventSent: boolean;

  // [auto_load_booking_questions_placement=personal_form] Mirrors booking-questions-view's
  // questionsLoaded gate: defaults to true so the legacy/flag-OFF/dedicated_step path renders
  // <app-personal-form> immediately, exactly as before (AC5 byte-for-byte). Only flips to false
  // while a pending auto-load fetch (triggered earlier at SERVICES_VIEW) is still in flight, so a
  // soon-to-be-appended question id is never missed by PersonalFormComponent's ONE-TIME ngOnInit
  // read of its customerFormFields @Input() (it has no ngOnChanges reactivity).
  protected customerFormFieldsReady = signal(true);
  protected readonly customerFormFieldsGhostElements = new Array<number>(2);
  // The customer-entered store-search postcode while the zip still holds it (drives the zip's
  // `cbw-zip-prefilled` class), otherwise null.
  protected readonly lockedStorePostcode = signal<string | null>(null);
  // Whether a saved customer existed when this view was created. Only that customer is re-saved
  // when the zip is filled; a first-time customer is saved on submit, as before.
  private readonly hasSavedUser: boolean;

  constructor(
    public bookingService: BookingService,
    private widgetService: WidgetService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private destroyRef: DestroyRef,
  ) {
    this.hasSavedUser = this.bookingService.loadAppointmentSelectedUserFromLocalStorage() === true;
    if (this.bookingService.selectedBookingData.selectedUser) {
      this.userInfo = this.bookingService.selectedBookingData.selectedUser;
    }
    if (!this.userInfo) {
      this.userInfo = new UserInfo();
    }
    if (this.bookingService.isMeetingTypeStepInFlow()) {
      this.showMeetingTypes = false;
    } else {
      this.showMeetingTypes = true;
    }
  }

  ngOnInit(): void {
    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_personal_data_start);
    this.isGTMEventSent = true;
    const config: WidgetConfModel = this.widgetService.widgetConf;
    this.meetingTypes = config?.context?.meeting_providers;
    if (config?.context?.customer_required?.length > 0) {
      this.customerRequiredFields = config?.context?.customer_required;
    } else {
      this.customerRequiredFields = [];
    }
    this.customerReadonlyFields = config?.context?.customer_readonly ?? [];
    this.requiredQuestionIds = config?.context?.required_booking_questions ?? [];
    this.customerPrefillValues = config?.context?.customer_prefill ?? {};

    // An explicit customer zip wins over the store-search postcode, which only fills a gap or
    // replaces the value it filled before. Runs before the child's own customer_prefill pass, which
    // happens in its ngOnInit.
    const state = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
    const storePostcode = this.bookingService.getCustomerEnteredStorePostcode();
    const previousZip = this.userInfo.zip;
    const previousMarker = state.personalFormZipFilledFromPostcode ?? null;
    const resolution = resolvePersonalFormZip({
      currentZip: previousZip,
      explicitCustomerZip: this.customerPrefillValues?.zip,
      storePostcode,
      filledFromPostcode: previousMarker,
    });
    this.userInfo.zip = resolution.zip;
    state.personalFormZipFilledFromPostcode = resolution.filledFromPostcode;
    // Written only on a change, so partners without the store-search postcode get no new write.
    if (resolution.filledFromPostcode !== previousMarker) {
      this.bookingService.setAppointmentStateInLocalStorage(state);
    }
    // Keep a saved customer in step with the fill, so a reload shows the current zip.
    if (this.hasSavedUser && resolution.zip !== previousZip) {
      this.bookingService.setAppointmentSelectedUserInLocalStorage(this.userInfo);
    }
    this.lockedStorePostcode.set(
      storePostcode && normalizePostcode(this.userInfo.zip) === normalizePostcode(storePostcode) ? storePostcode : null
    );

    if (this.bookingService.isAutoLoadInPersonalForm()) {
      // Wait for any resolve triggered earlier at SERVICES_VIEW to settle (resolves immediately
      // when nothing is pending — mirrors booking-questions-view.component.ts's own
      // settle-then-render dance) so the auto-loaded question ids are final before
      // PersonalFormComponent reads customerFormFields for its ONE-TIME ngOnInit fetch.
      this.customerFormFieldsReady.set(false);
      this.bookingService.whenAutoLoadSettled().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.buildCustomerFormFields(config);
        this.customerFormFieldsReady.set(true);
      });
    } else {
      this.buildCustomerFormFields(config);
    }
  }

  // Builds the personal-form field list, unioning two sources:
  //  • context.customer — standard customer fields (strings) AND, for BOOKING, inline
  //    booking-question ids (numbers) interleaved in the partner's chosen order
  //  • the effective auto-loaded question ids when auto_load_booking_questions_placement=personal_form
  // PersonalFormComponent fetches any numeric entry via getCustomFieldsByIds; required / prefill for
  // by-id questions apply automatically via the by-id context keys. Union+dedup by id, existing order
  // preserved. Where the auto-loaded ids land is auto_load_booking_questions_position:
  // 'bottom' (default/absent) appends them after everything, unchanged; 'top' prepends them at index
  // 0, i.e. ABOVE the customer contact fields as well — the loaded set depends on the customer's
  // service choice, so the partner cannot order it by hand in the setup page. An id the partner
  // already placed inline in context.customer keeps that explicit slot under both positions.
  // Placement=dedicated_step (default) or auto-load OFF: customerFormFields stays context.customer.
  private buildCustomerFormFields(config: WidgetConfModel): void {
    const contextFields: (string | number)[] = [
      ...(config?.context?.customer ?? []),
    ];

    if (!this.bookingService.isAutoLoadInPersonalForm()) {
      this.customerFormFields = contextFields;
      return;
    }

    if (this.bookingService.hasAutoLoadFetchFailed()) {
      // A failed auto-load fetch must never block the personal form (item 8) — skip the
      // auto-loaded ids for this render and log, rather than surfacing the dedicated-step's
      // retry/error screen here.
      LoggerService.warn('[auto_load_booking_questions_placement=personal_form] Auto-load fetch failed — rendering the personal form without the service-resolved questions.');
    }

    // Placement target, not the full effective set: ids the before-questions step
    // already asked must not repeat inside the personal form (before-mode OFF -> identical to
    // the previous getEffectiveBookingQuestionIds() read).
    const autoLoadedIds = this.bookingService.getPlacementTargetQuestionIds();
    if (!autoLoadedIds.length) {
      this.customerFormFields = contextFields;
      return;
    }

    const existingIds = new Set(contextFields.filter((field): field is number => typeof field === 'number'));
    const newIds = autoLoadedIds.filter(id => !existingIds.has(id));
    this.customerFormFields = this.bookingService.getAutoLoadBookingQuestionsPosition() === 'top'
      ? [...newIds, ...contextFields]
      : [...contextFields, ...newIds];
  }

  gotoPreviousPage(): void {
    // Back-nav follows the placement target: a non-empty target means a dedicated
    // AFTER step rendered before this form (legacy context questions, or the service-resolved
    // remainder in the combined before+auto-load mode) — go back to it. An empty target means
    // the previous step was slot selection (pure before-mode, or nothing to ask); placement=
    // personal_form never had a dedicated step to go back to. Each legacy mode reduces to its
    // previous branch byte-for-byte.
    if (!this.bookingService.isAutoLoadInPersonalForm() && this.bookingService.getPlacementTargetQuestionIds().length > 0) {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_AFTER, isUserInteracted: true });
    } else if (this.bookingService.isBeforeQuestionsMode() && !this.bookingService.isAutoLoadBookingQuestionsEnabled()) {
      this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
    } else {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.SLOTS_VIEW, isUserInteracted: true });
    }
  }

  onSubmitEvent(event: {
    userInfo: UserInfo,
    personalFormWidgetQuestions: CustomFieldModel[],
    hiddenCustomFields: CustomFieldModel[]
  }): void {
    if (event.hiddenCustomFields?.length > 0) {
      this.bookingService.hiddenCustomFields =
        this.bookingService.hiddenCustomFields.concat(event.hiddenCustomFields);
    }

    if (event.personalFormWidgetQuestions?.length > 0) {
      const personalFormCustomFieldValues:
        {
          custom_field_id: number,
          type: string,
          value?: any, file_ids?: any
        }[] = this.bookingService.setupFinalCustomFields(
          event.personalFormWidgetQuestions,
          this.bookingService.selectedBookingData.selectedCustomFieldValues
        );
      this.bookingService.selectedBookingData.selectedCustomFieldValues = personalFormCustomFieldValues;
    }

    this.bookingService.selectedBookingData.selectedUser = event.userInfo;

    LoggerService.log('this.bookingService.hiddenCustomFields ', this.bookingService.hiddenCustomFields);

    this.bookingService.selectedBookingData.selectedCustomFieldValues = this.bookingService.handleHiddenCustomFields(
      this.bookingService.selectedBookingData.selectedCustomFieldValues,
      this.bookingService.hiddenCustomFields
    );

    LoggerService.log('Final this.bookingService.selectedBookingData.selectedCustomFieldValues ',
      this.bookingService.selectedBookingData.selectedCustomFieldValues);
    LoggerService.log('event.userInfo ', event.userInfo);

    this.bookingService.setAppointmentSelectedUserInLocalStorage(
      this.bookingService.selectedBookingData.selectedUser
    );

    // false: this write is past the hideable steps, so re-arming would re-hide one the customer
    // completed manually after a failed resolution.
    this.bookingService.setCustomFieldValuesInLocalStorage(
      this.bookingService.selectedBookingData.selectedCustomFieldValues,
      false
    );

    this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_personal_data_end);

    this.nextPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_SUMMARY_VIEW, isUserInteracted: true });
  }
}
